import React from 'react';
import PropTypes from 'prop-types';
import { compose, withHandlers } from 'recompose';
import { PAGE_TYPES, PAGE_SUB_TYPES } from '@sparefoot/tracking-events';

import { scrollToElement } from 'lib/helpers';
import { getFilterParamForAmenity } from 'lib/search/parseAmenityFromPath';
import {
	unitSizesBySearchType,
	unitAmenities,
	facilityAmenities,
	amenities,
	storageBySizeBuckets,
} from 'config/units';

import { Link } from 'components/core/Link';
import { Paragraph, Text } from 'components/core/Typography';
import { Section } from 'components/core/Page';
import { Image } from 'components/core/Image';
import { ModalLink } from 'components/core/ModalLink';
import { Checkbox } from 'components/core/Checkbox';
import { Pill } from 'components/core/Pill';
import { FilterButtonWithTooltip } from 'components/search/FilterButton';

import { TrackingClient } from '@sparefoot/tracking-client';
import { encode } from 'querystring';
import enclosedUnitImage from 'assets/images/unit-icons/enclosed-unit-outlined.svg';

import './searchFilterContent.scss';

const renderAmenitiesListSection = (
	amenities,
	title,
	facetCounts,
	queryParams,
	handleInputChange,
	idTagName
) => (
	<Section className="filter-section amenity-filter">
		<Paragraph eyebrow>{title}</Paragraph>
		<ul className="checkbox-group">
			{amenities.map((amenity, index) => {
				const isInObject = JSON.stringify(facetCounts).includes(
					amenity.checkboxQueryParam
				);
				const isZero = facetCounts[amenity.checkboxQueryParam] === 0;
				const isChecked = queryParams[amenity.checkboxQueryParam];

				return (
					<li
						key={amenity.checkboxQueryParam}
						className="checkbox-item"
					>
						<Checkbox
							disabled={isInObject && isZero && !isChecked}
							id={`${idTagName}-amenity-filter-${index}`}
							label={amenity.label}
							input={{
								value: !!queryParams[
									amenity.checkboxQueryParam
								],
								onChange: handleInputChange(
									amenity.checkboxQueryParam
								),
							}}
						/>
					</li>
				);
			})}
		</ul>
	</Section>
);

export const enhance = compose(
	withHandlers({
		handleFilter: (props) => (searchParam, value) => {
			const newParams = {
				...props.queryParams,
				[searchParam]: value,
			};

			if (value) {
				// Only sends tracking event when user selects, not de-selects filters
				// Applies to all standard storage filters with this method
				TrackingClient.trackSearchFiltered({
					search_filters: encode(props.queryParams),
					search_label: `${searchParam}-${value}`,
					search_term: searchParam,
				});
			} else if (newParams.amenity && getFilterParamForAmenity(newParams.amenity) === searchParam) {
				// Clear the amenity param when unchecking the filter that corresponds
				// to the current amenity landing page context. This prevents
				// SearchPageWrapper from re-injecting the filter on the next render.
				newParams.amenity = undefined;
			}

			props.onFilter(newParams);
		},
	}),
	withHandlers({
		handleInputChange: (props) => (checkboxQueryParam) => (value) => {
			props.handleFilter(checkboxQueryParam, value);

			scrollToElement('.search-container', {
				top: 32,
				behavior: 'smooth',
			});
		},
	})
);

export function SearchFilterContent({
	handleFilter,
	handleInputChange,
	onShowUnitGuide,
	unitTypes,
	unitAmenities,
	facilityAmenities,
	queryParams,
	hideAmenities,
	cityUrl,
	pageType,
	facetCounts,
	unitBuckets,
}) {
	return (
		<div className="filter-content">
			{pageType === PAGE_SUB_TYPES.STORAGE_DEALS_LANDING && (
				<Link
					href={`${cityUrl}#search-results`}
					hideBorder
				>
					<Pill>
						<Text eyebrow>FIRST MONTH FREE ✕</Text>
					</Pill>
				</Link>
			)}

			{!!unitBuckets.length && (
				<Section className="filter-section size-filter">
					<Paragraph eyebrow>Unit sizes</Paragraph>
					<ul className="filter-group">
						{unitBuckets.map((type, index) => (
							<li
								key={type.value}
								className="filter-item"
							>
								<FilterButtonWithTooltip
									label={type.label}
									value={type.value}
									icon={type.icon}
									type="sqft"
									selected={type.value === queryParams.sqft}
									onClick={handleFilter}
									secondaryText={type.secondaryText}
									tooltipContent={type.tooltipContent}
									first={index == 0}
								/>
							</li>
						))}
					</ul>
					<div className="size-guide-modal-link">
						<Image src={enclosedUnitImage} alt="Info" />
						<ModalLink
							text="View our storage unit size guide"
							onClick={onShowUnitGuide}
						/>
					</div>
				</Section>
			)}

			{!hideAmenities && facetCounts && (
				<>
					{renderAmenitiesListSection(
						unitAmenities,
						'Unit Amenities',
						facetCounts,
						queryParams,
						handleInputChange,
						'unit'
					)}
					{renderAmenitiesListSection(
						facilityAmenities,
						'Facility Amenities',
						facetCounts,
						queryParams,
						handleInputChange,
						'facility'
					)}
				</>
			)}
		</div>
	);
}

SearchFilterContent.propTypes = {
	handleFilter: PropTypes.func.isRequired,
	handleInputChange: PropTypes.func.isRequired,
	onShowUnitGuide: PropTypes.func.isRequired,
	unitTypes: PropTypes.arrayOf(PropTypes.object),
	queryParams: PropTypes.object.isRequired,
	hideAmenities: PropTypes.bool,
	cityUrl: PropTypes.string,
	pageType: PropTypes.string,
	facetCounts: PropTypes.object,
	unitAmenities: PropTypes.arrayOf(PropTypes.object),
	facilityAmenities: PropTypes.arrayOf(PropTypes.object),
	unitBuckets: PropTypes.arrayOf(PropTypes.object),
};

SearchFilterContent.defaultProps = {
	unitTypes: unitSizesBySearchType['storage-only'],
	unitAmenities: unitAmenities,
	facilityAmenities: facilityAmenities,
	amenities: amenities,
	unitBuckets: storageBySizeBuckets['storage-only'],
};

export default enhance(SearchFilterContent);
